import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getHomeData } from "@/api/homeApi";
import { assetUrl } from "@/utils/assetUrl";

// 视频组件暂时隐藏，等有合适内容再打开
const SHOW_VIDEO = false;

export default function HeroBanner() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [banner, setBanner] = useState<any>(null);
  const [stats, setStats] = useState<any[]>([]);
  const [video, setVideo] = useState<any>(null);
  const [clientLogos, setClientLogos] = useState<string[]>([]);
  const [videoDuration, setVideoDuration] = useState("");

  useEffect(() => {
    getHomeData().then((res: any) => {
      if (res.banner) setBanner(res.banner);
      if (res.stats) setStats(res.stats);
      if (res.clientLogos) setClientLogos(res.clientLogos.map((c: any) => c.name));
      if (res.videos?.length > 0) setVideo(res.videos[0]);
    }).catch(() => {});
  }, []);

  // 自动获取视频时长
  useEffect(() => {
    if (!video?.videoUrl) return;
    const el = document.createElement("video");
    el.preload = "metadata";
    el.src = video.videoUrl;
    el.onloadedmetadata = () => {
      const sec = Math.round(el.duration);
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      setVideoDuration(`${m}:${s.toString().padStart(2, "0")}`);
    };
  }, [video?.videoUrl]);

  // 解析 title：按 \n 拆分，第一行白色，第二行蓝色渐变
  const titleLines = (banner?.title || "").split(/\\n|\n/).filter(Boolean);
  const titleLine1 = titleLines[0] || "";
  const titleLine2 = titleLines[1] || "";

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0B1B3D] pt-0">
        {/* Video Background — 仅本地/直链视频作为背景，外部嵌入(youtube/bilibili)不做背景以避免加载失败 */}
        {SHOW_VIDEO && video?.videoUrl && !video.videoUrl.includes("youtube") && !video.videoUrl.includes("bilibili") && !video.videoUrl.includes("embed") && (
          <video
            key={video.videoUrl}
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover object-center"
            autoPlay muted loop playsInline
            poster={video?.coverUrl || undefined}
          >
            <source src={video.videoUrl} type="video/mp4" />
          </video>
        )}

        {/* Dark overlay */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(11,27,61,0.88) 0%, rgba(11,27,61,0.72) 50%, rgba(11,27,61,0.80) 100%)" }}></div>
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(77,184,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(77,184,255,1) 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
        {/* Radial glow */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 60% at 30% 50%, rgba(26,86,219,0.18) 0%, transparent 70%)" }}></div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-32 lg:pt-36 pb-20">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left Content */}
            <div className="flex-1 w-full">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-full mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4db8ff] animate-pulse"></span>
                <span className="text-[#4db8ff] text-xs font-medium tracking-wide">
                  欧格登智能 · 企业级安全防护专家 · 深耕行业10年+
                </span>
              </div>

              <h1 className="text-3xl lg:text-5xl font-bold text-white leading-[2.2] mb-6">
                {/* SEO 友好的主语：始终对爬虫可见，视觉上仅在 lg 以下隐藏以避免与 banner.title 重复 */}
                <span className="sr-only">欧格登智能 - 企业安全解决方案提供商</span>
                {titleLine1}
                {titleLine2 && (
                  <>
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4db8ff] to-[#60a5fa]">
                      {titleLine2}
                    </span>
                  </>
                )}
                {banner?.subtitle && (
                  <>
                    <br />
                    {banner.subtitle}
                  </>
                )}
                {!titleLine1 && !titleLine2 && !banner?.subtitle && (
                  // 兜底：后端没下发 banner 时，H1 至少包含品牌 + 核心业务
                  <>
                    欧格登智能
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4db8ff] to-[#60a5fa]">
                      企业安全解决方案提供商
                    </span>
                  </>
                )}
              </h1>

              <p className="text-[#4db8ff]/90 text-sm lg:text-base font-medium mb-4 tracking-wide">
                信息安全 · IT基础架构 · 弱电智能化，三大领域一站式交付
              </p>

              {banner?.description && (
                <p className="text-gray-300 text-base lg:text-lg leading-relaxed mb-8 max-w-xl">
                  {banner.description}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-4 mb-14">
                <a href="#contact-form" className="px-8 py-3.5 bg-[#1a56db] hover:bg-[#1e40af] text-white font-semibold rounded-full transition-all duration-200 text-center whitespace-nowrap cursor-pointer text-sm">
                  {banner?.btnPrimary || "立即免费咨询"}
                </a>
                <Link to="/solutions" className="px-8 py-3.5 border border-white/25 hover:border-[#4db8ff]/70 text-white hover:text-[#4db8ff] font-semibold rounded-full transition-all duration-200 text-center whitespace-nowrap cursor-pointer text-sm backdrop-blur-sm">
                  {banner?.btnSecondary || "查看解决方案"}
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div key={stat.id ?? stat.label} className="text-center sm:text-left">
                    <div className="text-2xl lg:text-3xl font-bold text-[#4db8ff] mb-1">{stat.value}</div>
                    <div className="text-xs text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Visual - Video preview card */}
            {SHOW_VIDEO && (
            <div className="flex-shrink-0 w-full lg:w-[460px] flex items-center justify-center">
              <div className="relative w-full max-w-[420px]">
                <div className="relative rounded-2xl overflow-hidden border border-white/10 backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.05)" }}>
                  <div className="w-full h-56 lg:h-64 overflow-hidden relative">
                    {video?.coverUrl ? (
                      <img src={assetUrl(video.coverUrl)} alt={video?.title || "视频封面"} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1a56db]/30 to-[#0B1B3D] flex items-center justify-center">
                        <i className="ri-film-line text-4xl text-white/20"></i>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/60 to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center" onClick={() => setShowVideoModal(true)}>
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/15 border border-white/30 backdrop-blur-sm cursor-pointer hover:bg-white/25 transition-all duration-200">
                        <i className="ri-play-fill text-white text-2xl ml-1"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                      <span className="text-white text-xs font-medium bg-[#1a56db]/80 px-2.5 py-1 rounded-full backdrop-blur-sm">
                        {video?.title || "企业宣传片"}
                      </span>
                      <span className="text-white/70 text-xs">{videoDuration || video?.duration || ""}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white text-sm font-semibold">{video?.title || ""}</span>
                      <span className="text-[#4db8ff] text-xs">{video?.subtitle || ""}</span>
                    </div>
                    {video?.tags?.length > 0 && (
                      <div className="flex items-center gap-4">
                        {video.tags.map((tag: string) => (
                          <div key={tag} className="flex items-center gap-1.5">
                            <span className="text-gray-400 text-xs">{tag}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 flex items-center gap-1.5 px-3 py-2 bg-[#0B1B3D]/90 border border-[#1a56db]/40 rounded-xl backdrop-blur-sm">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                  <span className="text-white text-xs font-medium">实时安全监控中</span>
                </div>
                <div className="absolute -bottom-8 -left-4 flex items-center gap-2 px-3 py-2 bg-[#0B1B3D]/90 border border-[#1a56db]/40 rounded-xl backdrop-blur-sm">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-shield-check-fill text-[#4db8ff] text-sm"></i>
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">威胁已拦截</div>
                    <div className="text-[#4db8ff] text-xs">今日 +2,847 次</div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Right Visual - Hero 配图（未开启视频且后台配置了图片时展示，占位原视频位置） */}
            {!SHOW_VIDEO && banner?.heroImage && (
              <div className="flex-shrink-0 w-full lg:w-[560px] flex items-center justify-center">
                <div className="relative w-full max-w-[560px]">
                  <div className="rounded-2xl overflow-hidden border border-white/10 backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.05)" }}>
                    <img
                      src={assetUrl(banner.heroImage)}
                      alt={banner?.title || "欧格登智能"}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  {/* Floating badges 保持视觉氛围 */}
                  <div className="absolute -top-4 -right-4 flex items-center gap-1.5 px-3 py-2 bg-[#0B1B3D]/90 border border-[#1a56db]/40 rounded-xl backdrop-blur-sm">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                    <span className="text-white text-xs font-medium">实时安全监控中</span>
                  </div>
                  <div className="absolute -bottom-8 -left-4 flex items-center gap-2 px-3 py-2 bg-[#0B1B3D]/90 border border-[#1a56db]/40 rounded-xl backdrop-blur-sm">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-shield-check-fill text-[#4db8ff] text-sm"></i>
                    </div>
                    <div>
                      <div className="text-white text-xs font-semibold">威胁已拦截</div>
                      <div className="text-[#4db8ff] text-xs">今日 +2,847 次</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <span className="text-gray-500 text-xs">向下滚动</span>
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-line text-gray-500 text-sm"></i></div>
        </div>
      </section>

      {/* Client Logo Wall */}
      <div className="bg-white border-b border-gray-100 py-6 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0 text-center sm:text-left">
              <p className="text-xs text-gray-400 whitespace-nowrap">已为</p>
              <p className="text-sm font-bold text-[#1a56db] whitespace-nowrap">500+ 行业头部企业</p>
              <p className="text-xs text-gray-400 whitespace-nowrap">提供安全护航</p>
            </div>
            <div className="w-px h-10 bg-gray-200 hidden sm:block flex-shrink-0"></div>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 lg:gap-5 flex-1">
              {clientLogos.map((logo) => (
                <div key={logo} className="px-4 py-2 bg-[#F8F9FA] border border-gray-100 rounded-lg text-gray-500 text-sm font-medium hover:text-[#1a56db] hover:border-[#1a56db]/20 transition-all duration-200 cursor-pointer whitespace-nowrap">
                  {logo}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {SHOW_VIDEO && showVideoModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: "rgba(11,27,61,0.92)" }} onClick={() => setShowVideoModal(false)}>
          <div className="relative w-full max-w-4xl mx-4" onClick={(e) => e.stopPropagation()}>
            <button className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all duration-200 cursor-pointer" onClick={() => setShowVideoModal(false)}>
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ aspectRatio: "16/9" }}>
              {video?.videoUrl && (video.videoUrl.includes("youtube") || video.videoUrl.includes("bilibili") || video.videoUrl.includes("iframe") || video.videoUrl.includes("embed")) ? (
                <iframe
                  src={video.videoUrl}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  title={video?.title || "视频播放"}
                  style={{ border: "none" }}
                />
              ) : (
                <video className="w-full h-full object-cover" controls autoPlay playsInline poster={video?.coverUrl || undefined}>
                  {video?.videoUrl && <source src={video.videoUrl} type="video/mp4" />}
                  您的浏览器不支持视频播放
                </video>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between px-1">
              <div>
                <h3 className="text-white font-semibold text-base">{video?.title || ""}</h3>
                <p className="text-gray-400 text-sm mt-0.5">{video?.subtitle || ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-gray-400 text-xs">高清播放</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
